<template>
  <v-container v-if="$can('browse', 'YachtEquipmentPackCategory')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-12"
    >
      <v-col>
        <div class="headline">
          Equipment Pack Categories
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'YachtEquipmentPackCategory')"
          color="primary"
          class="ma-0"
          @click="openYachtEquipmentPackCategoryEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.name"
          solo
          label="Name"
          hide-details
          clearable
          class="mb-2"
          autocomplete="no"
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="yachtEquipmentPackCategories.meta.from"
          :to="yachtEquipmentPackCategories.meta.to"
          :total="yachtEquipmentPackCategories.meta.total"
          :last-page="yachtEquipmentPackCategories.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="yachtEquipmentPackCategories.data"
          :loading="yachtEquipmentPackCategories.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="yachtEquipmentPackCategories.meta.total"
          hide-default-footer
          must-sort
          class="elevation-5"
        >
          <template v-slot:item="{ item: category }">
            <tr
              class="clickable"
              @click="openYachtEquipmentPackCategoryEditDialog(category)"
            >
              <td>
                <v-avatar
                  size="20px"
                  :color="category.color"
                  class="mr-4"
                />
                {{ category.name }}
              </td>
              <td style="width: 1px">
                {{ category.order }}
              </td>
              <td>
                <v-chip
                  :color="category.required ? 'primary' : 'grey lighten-2'"
                  small
                >
                  {{ category.required ? 'Required' : 'Not Required' }}
                </v-chip>
              </td>
              <td>
                <span
                  v-if="category.is_navigare_option"
                  class="red--text font-weight-bold"
                >
                  Yes
                </span>
                <span
                  v-else
                  class="grey--text"
                >
                  No
                </span>
              </td>
              <td>
                <span
                  v-if="category.is_cdr"
                  class="red--text font-weight-bold"
                >
                  Yes
                </span>
                <span
                  v-else
                  class="grey--text"
                >
                  No
                </span>
              </td>
            </tr>
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no equipment pack categories at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  v-if="$can('create', 'YachtEquipmentPackCategory')"
                  color="primary"
                  class="ma-0"
                  small
                  @click="openYachtEquipmentPackCategoryEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
          <table-footer
            slot="footer"
            :last-page="yachtEquipmentPackCategories.meta.last_page"
            :page.sync="pagination.page"
          />
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <yacht-equipment-pack-category-edit-dialog
      :visible.sync="yachtEquipmentPackCategoryEditDialog"
      :yacht-equipment-pack-category="yachtEquipmentPackCategoryDirty"
      @yachtEquipmentPackCategory:created="yachtEquipmentPackCategoryCreated"
      @yachtEquipmentPackCategory:updated="yachtEquipmentPackCategoryUpdated"
      @yachtEquipmentPackCategory:deleted="yachtEquipmentPackCategoryDeleted"
    />
  </v-container>
</template>

<script>
import { cloneDeep } from 'lodash'
import paginationMixin from '@/mixins/paginationMixin'
import filterableMixin from '@/mixins/filterableMixin'
import YachtEquipmentPackCategory from '@/models/YachtEquipmentPackCategory'
import YachtEquipmentPackCategoryEditDialog
  from '@/components/settings/yachtEquipmentPackCategories/YachtEquipmentPackCategoryEditDialog'
import TableFooter from '@/components/helpers/TableFooter'

export default {
    name: 'YachtEquipmentPackCategoriesIndex',
    components: {
      YachtEquipmentPackCategoryEditDialog,
      TableFooter
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        yachtEquipmentPackCategories: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Order', value: 'order', sortable: true },
          { text: 'Eq. required', value: 'required', sortable: true },
          { text: 'Custom Option', value: 'is_navigare_option', sortable: true },
          { text: 'CDR cost', value: 'is_cdr', sortable: true }
        ],
        yachtEquipmentPackCategoryDirty: undefined,
        yachtEquipmentPackCategoryEditDialog: false,
        filtersClean: YachtEquipmentPackCategory.emptyFilters,
        filtersDirty: cloneDeep(YachtEquipmentPackCategory.emptyFilters)
      }
    },
    created() {
      this.sorting.sortBy = 'order'
      this.sorting.desc = false

      this.fetchYachtEquipmentPackCategories()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchYachtEquipmentPackCategories()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchYachtEquipmentPackCategories() {
        this.yachtEquipmentPackCategories.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await YachtEquipmentPackCategory.api.fetch(queryParams)

          this.yachtEquipmentPackCategories.data = result.data
          this.yachtEquipmentPackCategories.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachtEquipmentPackCategories.loading = false
        }
      },
      openYachtEquipmentPackCategoryEditDialog(yachtEquipmentPackCategory = undefined) {
        this.yachtEquipmentPackCategoryDirty = cloneDeep(yachtEquipmentPackCategory)
        this.yachtEquipmentPackCategoryEditDialog = true
      },
      clearFilters() {
        this.filters = cloneDeep(YachtEquipmentPackCategory.emptyFilters)
      },
      yachtEquipmentPackCategoryCreated(yachtEquipmentPackCategory) {
        this.yachtEquipmentPackCategories.data.unshift(yachtEquipmentPackCategory)

        this.$store.dispatch('getEquipmentPackCategories')
      },
      yachtEquipmentPackCategoryUpdated(yachtEquipmentPackCategory) {
        const index = this.yachtEquipmentPackCategories.data.findIndex(ye => ye.id === yachtEquipmentPackCategory.id)

        this.yachtEquipmentPackCategories.data.splice(index, 1, yachtEquipmentPackCategory)

        this.$store.dispatch('getEquipmentPackCategories')
      },
      yachtEquipmentPackCategoryDeleted(yachtEquipmentPackCategoryId) {
        const index = this.yachtEquipmentPackCategories.data.findIndex(ye => ye.id === yachtEquipmentPackCategoryId)

        this.yachtEquipmentPackCategories.data.splice(index, 1)

        this.$store.dispatch('getEquipmentPackCategories')
      }
    }
  }
</script>
